import { useAuthStore } from '@/store/authStore';
import { AlertCircle, AlertTriangle, Clock } from 'lucide-react';

export function TrialCountdownBanner() {
  const billingStatus = useAuthStore((s) => s.billingStatus);
  const trialEndsOn = useAuthStore((s) => s.trialEndsOn);

  if (billingStatus !== 'Trial' || !trialEndsOn) {
    return null;
  }

  const GRACE_PERIOD_DAYS = 3;
  const trialExpiredDate = new Date(trialEndsOn);
  const gracePeriodEndDate = new Date(trialExpiredDate.getTime() + GRACE_PERIOD_DAYS * 24 * 60 * 60 * 1000);
  const now = new Date();

  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const daysUntilExpiry = Math.ceil((trialExpiredDate.getTime() - now.getTime()) / millisecondsPerDay);
  const daysUntilGraceEnds = Math.ceil((gracePeriodEndDate.getTime() - now.getTime()) / millisecondsPerDay);

  const isExpired = now > trialExpiredDate;
  const isInGracePeriod = isExpired && now < gracePeriodEndDate;

  // No banner if more than 7 days remaining
  if (!isExpired && daysUntilExpiry > 7) {
    return null;
  }

  let bannerColor = '';
  let icon: React.ReactNode;
  let title = '';
  let message = '';

  if (isInGracePeriod) {
    // Red: In grace period
    bannerColor = 'bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800';
    icon = <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />;
    title = 'Trial Ended';
    message = `Your trial has ended. You have ${daysUntilGraceEnds} day${daysUntilGraceEnds === 1 ? '' : 's'} before access is restricted. Upgrade now.`;
  } else if (!isExpired && daysUntilExpiry <= 3) {
    // Amber: 3 days or less remaining
    bannerColor = 'bg-amber-50 border-amber-200 dark:bg-amber-950 dark:border-amber-800';
    icon = <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />;
    title = 'Trial Ending Soon';
    message = `Your trial ends in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}. Upgrade now to avoid interruption.`;
  } else if (!isExpired && daysUntilExpiry <= 7) {
    // Blue: 7 days or less
    bannerColor = 'bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800';
    icon = <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
    title = 'Free Trial';
    message = `Your free trial ends in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}. Upgrade to keep access.`;
  }

  if (!message) return null;

  return (
    <div className={`border-b ${bannerColor}`}>
      <div className="max-w-full px-4 py-3 sm:px-6">
        <div className="flex items-start gap-3">
          {icon}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{title}</p>
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{message}</p>
          </div>
          <a
            href="mailto:support@wrenchlab.net?subject=Upgrade%20Request"
            className="ml-3 flex-shrink-0 inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            Upgrade
          </a>
        </div>
      </div>
    </div>
  );
}
